/**
 * The nav tree, and the single source of truth for which pages exist.
 * Categories follow FRD-004 §5; every component exported from legion-ui's
 * `src/index.ts` appears exactly once.
 */

export interface NavItem {
  /** URL segment, kebab-case. */
  slug: string
  /** Display name, and the identifier a reader imports. */
  name: string
  /** One line, shown under the page title. */
  summary: string
}

export interface NavGroup {
  id: string
  title: string
  items: NavItem[]
}

export const NAV: NavGroup[] = [
  {
    id: 'overview',
    title: 'Overview',
    items: [
      {
        slug: '',
        name: 'Getting started',
        summary: 'Install legion-ui and render your first component.',
      },
      {
        slug: 'theming',
        name: 'Theming',
        summary: 'Tokens, schemes, and what LegionProvider writes to the DOM.',
      },
      {
        slug: 'breakpoints',
        name: 'Breakpoints',
        summary: 'The MUI v4 breakpoint table, unchanged.',
      },
      {
        slug: 'migrating-from-mui',
        name: 'Migrating from MUI',
        summary: 'What each Material-UI import becomes.',
      },
    ],
  },
  {
    id: 'layout',
    title: 'Layout',
    items: [
      {
        slug: 'stack',
        name: 'Stack',
        summary: 'Flex row or column with token-scale gaps.',
      },
      {
        slug: 'surface',
        name: 'Surface',
        summary: 'The glass panel every other surface is built on.',
      },
      {
        slug: 'card',
        name: 'Card',
        summary: 'A surface with content, actions and media slots.',
      },
      {
        slug: 'divider',
        name: 'Divider',
        summary: 'A hairline rule, horizontal or vertical.',
      },
      {
        slug: 'tile-grid',
        name: 'TileGrid',
        summary: 'Responsive auto-filling grid of equal tiles.',
      },
      {
        slug: 'collapse',
        name: 'Collapse',
        summary: 'Animated height transition for hidden content.',
      },
    ],
  },
  {
    id: 'inputs',
    title: 'Inputs',
    items: [
      {
        slug: 'button',
        name: 'Button',
        summary: 'Four variants, three sizes, icons and a loading state.',
      },
      {
        slug: 'icon-button',
        name: 'IconButton',
        summary: 'A square button holding a single icon.',
      },
      {
        slug: 'button-group',
        name: 'ButtonGroup',
        summary: 'Buttons joined into one segmented control.',
      },
      {
        slug: 'input',
        name: 'Input',
        summary: 'Text field with adornments and validation states.',
      },
      {
        slug: 'select',
        name: 'Select',
        summary: 'Native select styled to match Input.',
      },
      {
        slug: 'checkbox',
        name: 'Checkbox',
        summary: 'Binary and indeterminate states.',
      },
      { slug: 'switch', name: 'Switch', summary: 'An on/off toggle.' },
      {
        slug: 'rating',
        name: 'Rating',
        summary: 'Star rating, readable and interactive.',
      },
      {
        slug: 'form-field',
        name: 'FormField',
        summary: 'Label, control, helper text and error wiring.',
      },
      {
        slug: 'fieldset',
        name: 'Fieldset',
        summary: 'A labelled group of related fields.',
      },
    ],
  },
  {
    id: 'feedback',
    title: 'Feedback',
    items: [
      {
        slug: 'alert',
        name: 'Alert',
        summary: 'Inline status message in four severities.',
      },
      {
        slug: 'spinner',
        name: 'Spinner',
        summary: 'Indeterminate activity indicator.',
      },
      {
        slug: 'progress',
        name: 'Progress',
        summary: 'Determinate and indeterminate progress bars.',
      },
      {
        slug: 'badge',
        name: 'Badge',
        summary: 'A count or dot anchored to another element.',
      },
    ],
  },
  {
    id: 'navigation',
    title: 'Navigation',
    items: [
      {
        slug: 'tabs',
        name: 'Tabs',
        summary: 'A tablist with an animated indicator.',
      },
      {
        slug: 'link',
        name: 'Link',
        summary: 'An anchor with legion focus and hover treatment.',
      },
      {
        slug: 'accordion',
        name: 'Accordion',
        summary: 'Disclosure panels with summary and details.',
      },
      { slug: 'menu', name: 'Menu', summary: 'A floating list of actions.' },
    ],
  },
  {
    id: 'data-display',
    title: 'Data Display',
    items: [
      {
        slug: 'text',
        name: 'Text',
        summary: 'Every typographic variant and tone.',
      },
      {
        slug: 'icon',
        name: 'Icon',
        summary: 'Sizing and colour wrapper for any SVG.',
      },
      {
        slug: 'avatar',
        name: 'Avatar',
        summary: 'Image, initials or fallback, in three sizes.',
      },
      {
        slug: 'chip',
        name: 'Chip',
        summary: 'Compact pill, optionally clickable or deletable.',
      },
      { slug: 'tag', name: 'Tag', summary: 'A small static label.' },
      {
        slug: 'list',
        name: 'List',
        summary: 'Rows with icons, text and secondary actions.',
      },
      {
        slug: 'table',
        name: 'Table',
        summary: 'Table primitives with sticky headers.',
      },
    ],
  },
  {
    id: 'overlays',
    title: 'Overlays',
    items: [
      {
        slug: 'dialog',
        name: 'Dialog',
        summary: 'Modal with focus trap and escape handling.',
      },
      {
        slug: 'tooltip',
        name: 'Tooltip',
        summary: 'Hover and focus label on a floating element.',
      },
      {
        slug: 'popover',
        name: 'Popover',
        summary: 'Arbitrary content anchored to a trigger.',
      },
      {
        slug: 'legion-provider',
        name: 'LegionProvider',
        summary: 'Theme a subtree or the document.',
      },
    ],
  },
  {
    id: 'hooks',
    title: 'Hooks',
    items: [
      {
        slug: 'use-theme',
        name: 'useTheme',
        summary: 'Read the resolved theme.',
      },
      {
        slug: 'use-media-query',
        name: 'useMediaQuery',
        summary: 'Subscribe to a media query.',
      },
      {
        slug: 'use-breakpoint',
        name: 'useBreakpoint',
        summary: 'The active breakpoint key.',
      },
      {
        slug: 'use-click-away',
        name: 'useClickAway',
        summary: 'Fire when a click lands outside.',
      },
      {
        slug: 'use-focus-trap',
        name: 'useFocusTrap',
        summary: 'Keep focus inside a container.',
      },
      {
        slug: 'use-escape',
        name: 'useEscape',
        summary: 'Handle the Escape key.',
      },
      {
        slug: 'use-floating',
        name: 'useFloating',
        summary: 'Position an element against an anchor.',
      },
      { slug: 'cx', name: 'cx', summary: 'Conditional className joining.' },
      {
        slug: 'space',
        name: 'Spacing helpers',
        summary: 'resolveSpace and resolveSides.',
      },
      {
        slug: 'breakpoints-api',
        name: 'breakpoints',
        summary: 'The breakpoint table and mediaQuery().',
      },
      {
        slug: 'apply-theme',
        name: 'applyTheme',
        summary: 'Write theme tokens imperatively.',
      },
    ],
  },
]

/** Every page, flattened, in nav order. */
export const ALL_ITEMS: { group: NavGroup; item: NavItem }[] = NAV.flatMap(
  (group) => group.items.map((item) => ({ group, item }))
)

export function pathFor(slug: string): string {
  return slug === '' ? '/' : `/${slug}`
}

export function slugFromPath(path: string): string {
  return path.replace(/^\/+|\/+$/g, '')
}

export function findItem(slug: string) {
  return ALL_ITEMS.find((entry) => entry.item.slug === slug)
}
